import React, { useEffect, useState, useRef } from 'react';
import { Container, Card, Button, Alert, Spinner, Form } from 'react-bootstrap';
import useToken from '../../hooks/useToken';
import { useNavigate } from 'react-router-dom';
import useProgressRedirect from '../../hooks/useProgressRedirect';
import ProgressTracker from '../shared/ProgressTracker';
import { Timer } from '../shared/Timer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilePdf, faProjectDiagram } from '@fortawesome/free-solid-svg-icons';

function DocumentationReview() {
  const { token, refreshAccessToken } = useToken();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [timeLeft, setTimeLeft] = useState(null);

  const websiteId = useRef(null);
  const threatModelProvided = useRef(null);

  const navigate = useNavigate();

  useProgressRedirect("DOCUMENTATION_REVIEW");

  // Defensive date parsing helper
  const parseServerDate = (s) => {
    if (!s) return null;
    const d = new Date(s);
    return isNaN(d.getTime()) ? null : d;
  };

  useEffect(() => {
    setLoading(true);

    (async () => {
      try {
        // If everything is set, skip the requests
        if (timeLeft !== null && websiteId.current !== null && threatModelProvided !== null) {
          return;
        }

        const authToken = token || await refreshAccessToken();

        const assignRes = await fetch('/api' + `/assignment`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });
        const assignments = await assignRes.json();
        const assigned = assignments.find(w => w.order_index === 1);
        if (!assigned) {
          setError('No website assignment found');
          return;
        }

        if (websiteId.current === null) {
          websiteId.current = assigned.website_id
        }

        if (threatModelProvided.current === null) {
          // Use the assignment's flag to determine whether threat model exists
          threatModelProvided.current = !!assigned.threat_model_provided;
        }

        let limitMins = assigned.time_limit_document_minutes;
        let serverStarted = parseServerDate(assigned.document_review_started_at);
        // If the server hasn't set a document-specific time limit, fall back to
        // admin override or default 15/10 based on threat model.
        if (!limitMins) {
          limitMins = threatModelProvided.current ? 15 : 10;
        }

        if (serverStarted === null) {
          // Start the document review on the server and get the recorded time
          const startRes = await fetch(`/api/website/${assigned.website_id}/document-review/start`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${authToken}` },
          });

          const data = await startRes.json();
          serverStarted = parseServerDate(data?.assignment?.document_review_started_at) || new Date();
        }

        const currentTime = new Date();
        const diff = Math.floor((currentTime.getTime() - serverStarted.getTime()) / 1000);

        if (timeLeft === null) {
          // Clamp at 0
          setTimeLeft(Math.max(limitMins * 60 - diff, 0));
        }

      } catch (err) {
        console.error(err);
        setError('Failed to load documentation or assignment');
      }
    })();

    setLoading(false);
    return () => setLoading(false);
  }, [loading, refreshAccessToken, timeLeft, token])

  const handleContinue = async () => {
    try {
      setLoading(true);
      const authToken = token || await refreshAccessToken();
      // mark end of document review on server
      await fetch(`/api/website/${websiteId.current}/document-review/end`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${authToken}` },
      });

      // advance to WEBSITE1
      const res = await fetch('/api' + '/player/progress', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${authToken}` },
        body: JSON.stringify({ progress_step: 'WEBSITE1' }),
      });
      if (res.ok) {
        navigate('/website1', { replace: true });
      } else {
        const data = await res.json();
        setError(data.message || 'Failed to advance to website');
      }
    } catch (err) {
      console.error(err);
      setError('Progress update failed');
    } finally {
      setLoading(false);
    }
  };

  if (error) return <Container className="py-5"><Alert variant="danger">{error}</Alert></Container>;
  // If timeLeft hasn't been calculated yet, show a spinner to avoid showing 0:00 briefly
  if (timeLeft === null) return <Container className="py-5"><Spinner animation="border" /></Container>;

  return (
    
    <Container className="py-5">
      <ProgressTracker currentStep="documentation_review" />
      <Card className="shadow p-4">
        <h2 className="mb-3">📚 Documentation Review</h2>
        <p className="text-muted">Please use this time to read the project design documents.</p>
        <Timer initial={timeLeft} onFinish={handleContinue}/>

        <div className="container mb-3">
          <div className="row">
            <div className={threatModelProvided.current ? "col-md-6" : "col-md-12"}>
              <Button href={`/document-view/${websiteId.current}`} target="_blank" rel="noopener noreferrer" variant="primary" className="w-100 mb-2 mb-md-0">
                <FontAwesomeIcon icon={faFilePdf} /> Open Design Document Viewer
              </Button>
            </div>
            {threatModelProvided.current && (
              <div className="col-md-6">
                  <Button href={'/threatmodelviewer-page1'} target="_blank" rel="noopener noreferrer" variant="success" className="w-100">
                    <FontAwesomeIcon icon={faProjectDiagram} /> View Threat Model
                  </Button>
              </div>
            )}
          </div>
        </div>

        {!threatModelProvided && (
          <div className="text-muted">Threat model not available for this assignment. You will have 10 minutes.</div>
        )}

        <DRQuestion onCorrect={handleContinue} threatModelProvided={threatModelProvided.current}/>
      </Card>
    </Container>
  );
}

function DRQuestion({ onCorrect = () => {}, threatModelProvided }) {
  const [questionLists, setQuestionLists] = useState(null);
  const answers = useRef({});
  const { token, refreshAccessToken } = useToken();

  const [status, setStatus] = useState(0);

  const buttonStatus = [
    {"text": "Submit", "variant": "primary", "disabled": false},
    {"text": "Incorrect", "variant": "danger", "disabled": true},
    {"text": "Correct", "variant": "success", "disabled": true},
  ];

  useEffect(() => {
    if (questionLists === null) {
      (async () => {
        const authToken = token || await refreshAccessToken();
        const resp = await fetch('/api' + `/dr/questions`, {
          headers: { Authorization: `Bearer ${authToken}` },
        });

        const data = await resp.json();
        setQuestionLists(data.map((e) => {
          return {
            "id": e.id,
            "title": e.title,
            "choices": JSON.parse(e.serialized_choices),
            "tm_related": e.tm_related
          }
        }))
      })();
    }
  }, [questionLists, refreshAccessToken, token]);


  async function check() {
    const o = Object.entries(answers.current).map(([key, value]) => {
      return {
        "id": key,
        "idx": value,
      }
    });

    const authToken = token || await refreshAccessToken();

    const res = await fetch(`/api/dr/answers`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${authToken}`, "content-type": "application/json"},
      body: JSON.stringify(o),
    });

    const data = await res.json();
    if (!data.correct) {
      setStatus(1);
      setTimeout(() => {
        setStatus(0);
      }, 1000);
    } else {
      setStatus(2);
      setTimeout(() => {
        onCorrect();
      }, 1000);
    }
  }

  if (questionLists === null) {
    return (<></>)
  }

  return (<>
    <Form onSubmit={() => {}}>
      {questionLists.filter((q) => !q.tm_related || threatModelProvided).map((q, idx) => {
        return (
          <Form.Group key={q.id} className="mb-4">
            <Form.Label><strong>{idx + 1}. {q.title}</strong></Form.Label>
            {q.choices.map((c, idx) => {
              return (
                <Form.Check
                  key={idx}
                  type="radio"
                  name={q.id}
                  label={c}
                  value={c}
                  onChange={() => answers.current[q.id] = idx}
                />
              )
            })}
          </Form.Group>
        )
      })}
    </Form>
    <div className="d-flex gap-2 mt-3">
      <Button variant={buttonStatus[status].variant} onClick={check} disabled={buttonStatus[status].disabled}>
        {buttonStatus[status].text}
      </Button>
    </div>
  </>)
}

export default DocumentationReview;
